import { Group } from '@mantine/core';
import { ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { getCurrency } from '../../../../helpers/currency-helpers';
import { useFinancialsStore } from '../../../../store/financials-store/financials-store';
import { setViewBy } from '../../../../store/financials-store/financials-store.actions';
import { ChartType, ViewBy } from '../../../../store/financials-store/financials-store.type';
import { useGlobalStore } from '../../../../store/global-store/global-store';
import { newCOLORS } from '../../../../styles/colors';
import { Button } from '../../../../ui-library/button/button';
import { Icon } from '../../../../ui-library/icon/icon';
import { Select } from '../../../../ui-library/select/select';
import { getViewByLabels } from './shared.type';

function ChartDisplayTypeButtonSet({
  onSetLineChart,
  onSetPieChart,
  onSetBarChart,
  selectedChartType,
  onExportChart,
  showSelectViewBy,
  floraButton,
}: {
  onSetLineChart?: () => void;
  onSetPieChart?: () => void;
  onSetBarChart: () => void;
  selectedChartType: ChartType;
  onExportChart: () => Promise<void>;
  showSelectViewBy?: boolean;
  floraButton?: ReactNode;
}) {
  const { t } = useTranslation();
  const viewBy = useFinancialsStore((state) => state.viewBy);
  const organization = useGlobalStore((state) => state.organization);
  const { code: currencyCode } = getCurrency(organization);
  const viewByLabels = useMemo(() => getViewByLabels(t), [t]);

  const viewByOptions = Object.values(ViewBy).map((viewBy) => {
    const baseLabel = viewByLabels[viewBy];
    const label =
      viewBy === ViewBy.TasksInUSD || viewBy === ViewBy.PointsInUSD ? `${baseLabel} in ${currencyCode}` : baseLabel;

    return { label, value: viewBy };
  });
  return (
    <Group mb={12} justify="space-between">
      <Group>
        {onSetLineChart && (
          <StyledButton
            label={t('common.charts.line_chart')}
            iconName="stacked_line_chart"
            onClick={onSetLineChart}
            selected={selectedChartType === ChartType.Line}
          />
        )}
        {onSetPieChart && (
          <StyledButton
            label={t('common.charts.pie_chart')}
            iconName="pie_chart"
            onClick={onSetPieChart}
            selected={selectedChartType === ChartType.Pie}
          />
        )}
        <StyledButton
          label={t('common.charts.bar_chart')}
          iconName="bar_chart"
          onClick={onSetBarChart}
          selected={selectedChartType === ChartType.Bar}
        />
      </Group>
      <Group>
        {showSelectViewBy && (
          <Select
            value={viewBy}
            options={viewByOptions}
            onChange={(value) => setViewBy(value as ViewBy)}
            style={{ width: '150px' }}
            size="xs"
          />
        )}
        {floraButton && floraButton}
        <StyledButton label={t('common.actions.export')} iconName="download" onClick={onExportChart} selected={true} />
      </Group>
    </Group>
  );
}

export function StyledButton({
  label,
  iconName,
  onClick,
  selected,
}: {
  label: string;
  iconName: string;
  onClick: () => void;
  selected: boolean;
}) {
  return (
    <Button
      variant={selected ? 'secondary' : 'outline'}
      radius="xl"
      size="xs"
      leftSection={
        <Icon
          name={iconName}
          color={selected ? newCOLORS.white : newCOLORS.gray}
          size={16}
          style={{ cursor: 'pointer' }}
        />
      }
      onClick={onClick}
    >
      {label}
    </Button>
  );
}

export { ChartDisplayTypeButtonSet };
